import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, useRef, useCallback } from 'react';
import styled from '@emotion/styled';
import { Route, Switch, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ActivityRulesContext } from 'ts-activities/ActivityRulesContext';
import { breakpoints, gutters, isMobile, useWindowSize } from 'app/spacing';
import KizenTypography from 'app/kizentypo';
import Loader from 'components/Kizen/Loader';
import { Nav } from 'components/Navigation/NavLayout';
import navItems from './navigation';
import { ControlBarControl } from './ControlBar';
import { useSetTitleOnLoad } from 'hooks/useSetTitleOnLoad';
import { ActivitySharingContext } from 'ts-activities/ActivitySharingContext';
import useCan from 'hooks/useCan';
import { PermissionsError } from 'services/errors';
import { snakeToCamelCaseKeys } from 'services/helpers';
import {
  useActivityQuery,
  useActivityFieldsQuery,
} from '__queries/models/activities-ts';
import { ACTIVITIES } from '__queries/query-keys';
import { TABS } from '__pages/ActivitySettings/pages/ScheduledAndLogged/constants';
import UUID from 'utility/UUID';
import { ScheduledLoggedNavigation } from './pages/ScheduledAndLogged/Navigation';

const MobileContainer = styled.div`
  padding: ${gutters.standard};
  text-align: center;
`;
const INITIAL_ERROR_MESSAGES = {
  name: '',
  redirectUrl: '',
  webhookUrl: '',
  calendarSyncDuration: '',
  calendarSyncTitle: '',
  show: false,
};

const setActivityTitle = (title, { name = '' } = {}) =>
  name ? `${name} - ${title}` : title;

const addIdToLayoutItem = (item) => {
  if (item.id) {
    return item;
  }

  if (item.type === 'field') {
    return { ...item, id: item.fieldId };
  } else {
    return { ...item, id: UUID.generate() };
  }
};

const buildInternalNames = (layout) => {
  const result = layout.reduce((acc, item) => {
    if (item.type === 'section') {
      acc[item.id] = item.internalName;
    }
    return acc;
  }, {});

  return result;
};

export default function ActivitySettingsPage({ title }) {
  const { t } = useTranslation();

  const params = useParams();
  const [errorMessages, setErrorMessages] = useState(INITIAL_ERROR_MESSAGES);
  /**
   * @typedef {import('./types').ActivityState} ActivityState
   * @type {[ActivityState, React.Dispatch<React.SetStateAction<ActivityState>>]}
   */
  const [activity, setActivity] = useState({});
  const [textBlockInternalNames, setTextBlockInternalNames] = useState({});
  const queryClient = useQueryClient();

  const activitiesAccess = useCan({
    view: 'sections',
    for: 'activities_section',
  });

  useSetTitleOnLoad(setActivityTitle(title(t), activity));

  const [customLayoutSelectedTab, setCustomLayoutSelectedTab] = useState(
    TABS.scheduled
  );
  const { id: activityObjectId } = params;
  const { width } = useWindowSize();
  const mobileDisplay = isMobile(width, breakpoints.lg);

  const activityQuery = useActivityQuery(activityObjectId, {
    select: (act) => {
      return snakeToCamelCaseKeys(act);
    },
  });

  const activityFieldsQuery = useActivityFieldsQuery(activityObjectId, {
    select: (fields) => {
      return snakeToCamelCaseKeys(fields);
    },
  });

  const { name } = activityQuery.data ?? {};
  const activityNameRef = useRef(activityQuery.data?.name ?? '');
  const prevActivityData = useRef();
  const prevActivityFieldsData = useRef();

  const initializeData =
    (!prevActivityData.current || !prevActivityFieldsData.current) &&
    Boolean(activityQuery.data) &&
    Boolean(activityFieldsQuery.data);

  prevActivityData.current = activityQuery.data;
  prevActivityFieldsData.current = activityFieldsQuery.data;

  useEffect(() => {
    if (activityQuery.isSuccess) {
      activityNameRef.current = name;
    }
  }, [activityQuery.isSuccess, name]);

  useEffect(() => {
    if (initializeData) {
      const act = prevActivityData.current;
      const fields = prevActivityFieldsData.current;

      const layout = act.layout.map(addIdToLayoutItem);

      setActivity({ ...act, fields, layout });
      setTextBlockInternalNames(buildInternalNames(layout));
    }
  }, [initializeData]);

  const handleUpdateActivity = useCallback(
    (activity, fields) => {
      setActivity(activity);
      queryClient.setQueryData(ACTIVITIES.ACTIVITY(activityObjectId), activity);

      if (fields) {
        queryClient.setQueryData(
          ACTIVITIES.ACTIVITY_FIELDS(activityObjectId),
          fields
        );
      }
    },
    [activityObjectId, queryClient]
  );

  const handleSetActivity = useCallback((activity) => {
    const layout = activity.layout.map(addIdToLayoutItem);
    setActivity({ ...activity, layout });
    setTextBlockInternalNames(buildInternalNames(layout));
  }, []);

  if (!activitiesAccess) {
    throw new PermissionsError();
  }

  if (mobileDisplay) {
    return (
      <MobileContainer>
        <KizenTypography>
          {t(
            'This page has not been optimized for mobile. Please revisit on a desktop to receive the best experience.'
          )}
        </KizenTypography>
      </MobileContainer>
    );
  }

  if (activity.id !== activityObjectId) {
    return <Loader loading />;
  }

  return (
    <ActivityRulesContext
      key={`${params.id}_${params.subpage}`}
      activityId={params.id}
      isActive={params.subpage === 'rules'}
      setActivity={handleSetActivity}
    >
      <ActivitySharingContext activityId={params.id} setActivity={setActivity}>
        <Nav.Container>
          <Nav.TopNavRow>
            <ControlBarControl
              name={activityNameRef}
              setErrorMessages={setErrorMessages}
              activity={activity}
              setCustomLayoutSelectedTab={setCustomLayoutSelectedTab}
              customLayoutSelectedTab={customLayoutSelectedTab}
            />
          </Nav.TopNavRow>
          <Nav.SubNavigationRow>
            {Object.values(navItems).map((route, idx, arr) => {
              const common = {
                isLast: idx === arr.length - 1,
                numberOfLinks: arr.length,
                params,
              };
              return (
                <Nav.SubNavigationLink
                  key={route.path}
                  route={route}
                  {...common}
                />
              );
            })}
          </Nav.SubNavigationRow>
          {params.subpage === 'scheduled-and-logged' && (
            <Nav.Row>
              <ScheduledLoggedNavigation
                tab={customLayoutSelectedTab}
                onSelectTab={setCustomLayoutSelectedTab}
              />
            </Nav.Row>
          )}
        </Nav.Container>
        <Switch>
          {Object.values(navItems).map(
            ({ component: Component, path, exact }) =>
              Component ? (
                <Route
                  key={path}
                  path={path}
                  exact={exact !== false}
                  render={() => (
                    <Component
                      activityObjectId={activityObjectId}
                      activity={activity}
                      textBlockInternalNames={textBlockInternalNames}
                      setTextBlockInternalNames={setTextBlockInternalNames}
                      setActivity={handleUpdateActivity}
                      errorMessages={errorMessages}
                      setErrorMessages={setErrorMessages}
                      activityNameRef={activityNameRef}
                      loading={
                        activityQuery.isLoading || activityFieldsQuery.isLoading
                      }
                      customLayoutSelectedTab={customLayoutSelectedTab}
                    />
                  )}
                />
              ) : null
          )}
        </Switch>
      </ActivitySharingContext>
    </ActivityRulesContext>
  );
}
